import type { DiffStatus } from '~/components/common/enrichment-diff-table/diff-status.util';
import { HighlightCardList } from '~/components/common/enrichment-diff-table/highlight-card-list';
import { ProductDetailCardList } from '~/components/common/enrichment-diff-table/product-detail-card-list';

import type {
  ProductDetailItem,
  ProductQaItem,
  StructuredContentValue,
} from '../types/content-scoring.types';

// ---------------------------------------------------------------------------
// Render helpers — visual style mirrors OptContent in product-detail-page.tsx
// ---------------------------------------------------------------------------

/** Attribution-list fields whose API value is `string[]` — rendered as a bullet list.
 *  `productHighlight` is handled separately as tinted card rows (see `HighlightCardList`). */
const STRING_ARRAY_FIELD_KEYS = new Set<string>(['relatedProduct', 'variantOption']);

/** Body type for each density: the dense scale suits a spreadsheet-style grid, the default suits the wide preview tables. */
const bodyClass = (dense: boolean) => (dense ? 'text-xs leading-4' : 'text-sm leading-relaxed');

/**
 * `string[]` fields → bullet list.
 * Mirrors the `bullets` content-type renderer in OptContent (product-detail-page.tsx).
 */
function StringArrayField({
  items,
  dense = false,
  testId,
}: {
  items: string[];
  dense?: boolean;
  /** E2E `data-testid` for the list. */
  testId?: string;
}) {
  return (
    <ul data-testid={testId} className={dense ? 'space-y-0.5' : 'space-y-1.5 pl-1'}>
      {items.map((item, i) => (
        <li key={i} className={`flex items-start gap-1.5 ${bodyClass(dense)}`}>
          <span
            className={`shrink-0 rounded-full bg-neutral-400 ${dense ? 'mt-1.5 h-1 w-1' : 'mt-2 h-1.5 w-1.5'}`}
          />
          {item}
        </li>
      ))}
    </ul>
  );
}

/**
 * `structuredTitle` / `structuredDescription` — extract `.content` and render
 * as plain text. `digital_source_type` is metadata and not shown in the table.
 */
function StructuredTextField({
  value,
  dense = false,
  testId,
}: {
  value: StructuredContentValue;
  dense?: boolean;
  /** E2E `data-testid` for the text. */
  testId?: string;
}) {
  return (
    <p data-testid={testId} className={bodyClass(dense)}>
      {value.content}
    </p>
  );
}

/**
 * `productQa` → Q&A pairs.
 * Mirrors the `qa` content-type renderer in OptContent — question is muted,
 * answer is font-medium.
 */
function ProductQaField({
  items,
  dense = false,
  testId,
}: {
  items: ProductQaItem[];
  dense?: boolean;
  /** E2E `data-testid` for the Q&A list. */
  testId?: string;
}) {
  return (
    <div data-testid={testId} className={dense ? 'space-y-1.5' : 'space-y-3'}>
      {items.map((item, i) => (
        <div key={i} className={`space-y-0.5 ${bodyClass(dense)}`}>
          <p className="text-muted-foreground">{item.q}</p>
          <p className="font-medium">{item.a}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface EnrichedFieldCellProps {
  /** API field name — determines the rendering strategy. */
  fieldName: string;
  /** Raw value from the product object; may be any enriched shape. */
  value: unknown;
  /** Diff status for `productHighlight`/`productDetail`'s card tint. Defaults to `'unchanged'` (neutral) — used by the Before row, which has no per-item status. */
  status?: DiffStatus;
  /**
   * Compact type and spacing for the feed-vs-enriched compare grid, whose
   * columns are roughly half the width of the preview tables'. Off by default so
   * the tables keep their existing presentation.
   */
  dense?: boolean;
  /** E2E `data-testid` for the rendered value; the empty dash is `${testId}-empty`. */
  testId?: string;
}

/**
 * Renders one enriched field value inside a table cell, using the same visual
 * style as OptContent (product-detail-page.tsx) adapted for the review-refine
 * API's JSON format (real arrays/objects rather than pipe-delimited strings):
 * string[] → bullet list, structuredTitle/Description → content text,
 * productDetail → attribute pairs, productQa → Q&A pairs, string → plain text.
 *
 * The Meta Ads API serializes structured fields (productDetail, productHighlight,
 * questionAndAnswer) as JSON strings rather than parsed objects. A JSON-parse
 * step runs first so downstream renderers always see the correct JS type.
 */
export function EnrichedFieldCell({
  fieldName,
  value,
  status,
  dense = false,
  testId = 'enriched-field-cell',
}: EnrichedFieldCellProps) {
  if (value == null) {
    return (
      <span data-testid={`${testId}-empty`} className={`text-gray-400 ${bodyClass(dense)}`}>
        —
      </span>
    );
  }

  // Meta Ads returns structured fields as JSON-encoded strings. Attempt to parse
  // so the downstream array/object renderers work correctly. Non-JSON strings are
  // left untouched — the plain-text fallback at the bottom handles them.
  const resolved: unknown =
    typeof value === 'string' &&
    (value.trimStart().startsWith('[') || value.trimStart().startsWith('{'))
      ? (() => {
          try {
            return JSON.parse(value) as unknown;
          } catch {
            return value;
          }
        })()
      : value;

  // productHighlight — individual tinted card rows instead of a bullet list
  if (fieldName === 'productHighlight' && Array.isArray(resolved)) {
    return (
      <HighlightCardList
        items={resolved as string[]}
        status={status ?? 'unchanged'}
        dense={dense}
      />
    );
  }

  // string[] fields — bullet list (mirrors OptContent 'bullets')
  if (STRING_ARRAY_FIELD_KEYS.has(fieldName) && Array.isArray(resolved)) {
    return <StringArrayField items={resolved as string[]} dense={dense} testId={testId} />;
  }

  // structuredTitle / structuredDescription — extract .content
  if (
    (fieldName === 'structuredTitle' || fieldName === 'structuredDescription') &&
    typeof resolved === 'object' &&
    !Array.isArray(resolved) &&
    resolved !== null &&
    'content' in resolved
  ) {
    return (
      <StructuredTextField
        value={resolved as StructuredContentValue}
        dense={dense}
        testId={testId}
      />
    );
  }

  // productDetail — individual card rows (attribute name + value), grouped by attribute name
  if (fieldName === 'productDetail' && Array.isArray(resolved)) {
    return (
      <ProductDetailCardList
        items={resolved as ProductDetailItem[]}
        status={status ?? 'unchanged'}
        dense={dense}
      />
    );
  }

  // questionAndAnswer — array of { q, a } pairs (mirrors OptContent 'qa')
  if (fieldName === 'questionAndAnswer' && Array.isArray(resolved)) {
    return <ProductQaField items={resolved as ProductQaItem[]} dense={dense} testId={testId} />;
  }

  // plain string fallback (description, etc.) — mirrors OptContent 'text'
  if (typeof resolved === 'string') {
    const parts = resolved
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length > 1) {
      return <StringArrayField items={parts} dense={dense} testId={testId} />;
    }
    return (
      <p data-testid={testId} className={bodyClass(dense)}>
        {resolved}
      </p>
    );
  }

  return (
    <span data-testid={`${testId}-empty`} className={`text-gray-400 ${bodyClass(dense)}`}>
      —
    </span>
  );
}
